// Thin API client. In dev, Vite proxies /api → http://localhost:8000.
// In Cloud Run, the web service points VITE_API_URL at the api service.

import type { AdapterName } from "./adapters";

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

// RunPod direct endpoint for chat (bypasses Cloud Run network restriction)
const RUNPOD_URL = (import.meta.env.VITE_RUNPOD_URL as string | undefined) ?? "";
const RUNPOD_KEY = (import.meta.env.VITE_RUNPOD_KEY as string | undefined) ?? "";

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface ChatRequest {
  adapter: AdapterName;
  messages: ChatMessage[];
  max_tokens?: number;
  temperature?: number;
  stream?: boolean;
  /** tutor = live session; assistant = Ask Maya chatbot sidebar */
  persona?: "tutor" | "assistant";
}

export interface ChatResponse {
  adapter: AdapterName;
  content: string;
  finish_reason: string;
  latency_ms: number;
  model?: string;
  adapter_used?: boolean;
}

export interface SessionSummary {
  questions_attempted: number;
  questions_correct: number;
  predicted_score: number;
  weak_topics: string[];
  streak_days: number;
}

export interface LearnVideo {
  id: string;
  youtube_id: string;
  title: string;
  channel: string;
  channel_url: string;
  url: string;
  tag: string;
  published_at?: string;
  thumbnail?: string;
}

export interface LearnVideosResponse {
  videos: LearnVideo[];
  source: string;
}

export interface PracticeQuestion {
  id: string;
  sub_type: string;
  topic: string;
  difficulty: 1 | 2 | 3 | 4;
  stem: string;
  passage?: string;
  choices: { label: "A" | "B" | "C" | "D" | "E" | "F"; text: string }[];
  correct_answer?: "A" | "B" | "C" | "D" | "E" | "F";
}

export interface OfficialExamResource {
  id: string;
  name: string;
  description: string;
  url: string;
  price: string;
  questions: string;
}

export interface ExamBlueprintSummary {
  id: string;
  title: string;
  subtitle: string;
  authenticity: "official" | "sigma";
  total_minutes: number;
  total_questions: number;
  sections: { id: string; label: string; minutes: number; question_count: number }[];
}

export interface ExamSessionState {
  session_id: string;
  exam_id: string;
  title: string;
  status: "active" | "section_break" | "completed";
  current_section_index: number;
  current_question_index: number;
  total_questions: number;
  section_label: string;
  section_minutes: number;
  section_question_count: number;
  questions_answered: number;
  time_remaining_seconds?: number;
}

export interface ExamQuestionResponse {
  session: ExamSessionState;
  question: PracticeQuestion;
  question_number: number;
  section_number: number;
}

export interface ExamSubmitAnswerResponse {
  session: ExamSessionState;
  correct: boolean;
  correct_answer?: "A" | "B" | "C" | "D" | "E" | "F";
  finished_section: boolean;
  exam_complete: boolean;
}

export interface TopicBreakdown {
  topic: string;
  section: string;
  correct: number;
  total: number;
  percent: number;
}

export interface DifficultyBreakdown {
  difficulty: number;
  correct: number;
  total: number;
  percent: number;
}

export interface TimingAnalysis {
  total_seconds: number;
  avg_seconds: number;
  fastest_seconds: number;
  slowest_seconds: number;
  slowest_topic?: string;
}

export interface ExamResultsResponse {
  session_id: string;
  exam_id: string;
  title: string;
  total_correct: number;
  total_questions: number;
  overall_percent: number;
  section_scores: {
    section_id: string;
    label: string;
    correct: number;
    total: number;
    percent: number;
  }[];
  estimated_score_range: [number, number];
  weak_topics: string[];
  topic_breakdown: TopicBreakdown[];
  difficulty_breakdown: DifficultyBreakdown[];
  timing?: TimingAnalysis;
  strengths: string[];
}

export interface AgentTraceEvent {
  agent: string;
  action: string;
  detail: string;
  section?: string;
}

export interface GeneratedExamResponse {
  session_id: string;
  title: string;
  total_questions: number;
  sections: { id: string; label: string; minutes: number; question_count: number }[];
  trace: AgentTraceEvent[];
}

export interface AttemptSectionScore {
  section_id: string;
  label: string;
  correct: number;
  total: number;
  percent: number;
}

export interface AttemptRecord {
  id: string;
  student_id: string;
  created_at: number;
  exam_id?: string;
  title: string;
  total_correct: number;
  total_questions: number;
  overall_percent: number;
  est_low: number;
  est_high: number;
  total_seconds: number;
  section_scores: AttemptSectionScore[];
  weak_topics: string[];
  strengths: string[];
}

export type AttemptCreate = Omit<AttemptRecord, "id" | "created_at">;

export interface BankStats {
  total_questions: number;
  by_section: Record<string, number>;
  verbal: {
    total_questions: number;
    critical_reasoning: number;
    reading_comprehension: number;
    rag_chunks: number;
  };
}

export interface AgentTraceEvent {
  agent: string;
  action: string;
  detail: string;
  section?: string;
}

export interface GeneratedExamResponse {
  session_id: string;
  title: string;
  total_questions: number;
  sections: { id: string; label: string; minutes: number; question_count: number }[];
  trace: AgentTraceEvent[];
}

export interface RetrievalHit {
  id: string;
  section: string;
  sub_type: string;
  topic: string;
  difficulty: number;
  stem: string;
  score: number;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError(res.status, body || res.statusText);
  }
  return res.json() as Promise<T>;
}

// Direct RunPod chat — browser calls RunPod H100 directly with streaming
async function chatDirect(req: ChatRequest): Promise<ChatResponse> {
  if (!RUNPOD_URL || !RUNPOD_KEY) return request<ChatResponse>("/chat", {
    method: "POST", body: JSON.stringify(req),
  });
  const msgs = req.messages.find(m => m.role === "system")
    ? req.messages
    : [{
        role: "system" as const,
        content:
`You are Maya, a warm GMAT tutor on a live voice call with a student.
STRICT FORMAT RULES:
- Reply in 1-2 short sentences. Maximum 35 words total.
- Plain spoken English only. No markdown. No lists. No LaTeX. No asterisks. No symbols.
- Sound like a real human tutor speaking out loud, not writing.
- For complex topics, give the core insight in one sentence, then ask one short follow-up.
- Never explain everything at once — keep the conversation flowing.`,
      }, ...req.messages];
  const t0 = Date.now();
  const res = await fetch(`${RUNPOD_URL}/chat/completions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${RUNPOD_KEY}`,
    },
    body: JSON.stringify({
      model: "qwen/qwen2.5-1.5b-instruct",
      messages: msgs,
      max_tokens: req.max_tokens ?? 120,   // ~ 2 sentences
      temperature: req.temperature ?? 0.3,  // lower = faster + more focused
      top_p: 0.85,
      stream: false,
    }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  const data = await res.json() as { choices: { message: { content: string }, finish_reason: string }[] };
  return {
    adapter: req.adapter,
    content: data.choices[0].message.content,
    finish_reason: data.choices[0].finish_reason,
    latency_ms: Date.now() - t0,
  };
}

export const api = {
  health: () => request<{ status: string }>("/health"),
  /** Always routes through the orchestrator API (LoRA adapters + math grounding). */
  tutorChat: (req: ChatRequest) =>
    request<ChatResponse>("/chat", { method: "POST", body: JSON.stringify(req) }),
  chat: (req: ChatRequest) =>
    RUNPOD_URL
      ? chatDirect(req)
      : request<ChatResponse>("/chat", { method: "POST", body: JSON.stringify(req) }),
  practiceQuestion: (topic?: string, difficulty?: number) =>
    request<PracticeQuestion>(
      `/practice/question?${new URLSearchParams({
        ...(topic ? { topic } : {}),
        ...(difficulty ? { difficulty: String(difficulty) } : {}),
      }).toString()}`,
    ),
  hint: (questionId: string, level: 1 | 2 | 3) =>
    request<{ hint: string }>("/practice/hint", {
      method: "POST",
      body: JSON.stringify({ question_id: questionId, level }),
    }),
  verify: (questionId: string, answer: string, work: string) =>
    request<{
      correct: boolean;
      explanation: string;
      error_type?: string;
    }>("/practice/verify", {
      method: "POST",
      body: JSON.stringify({ question_id: questionId, answer, work }),
    }),
  predictScore: (input: {
    quant_correct_pct: number;
    verbal_correct_pct: number;
    di_correct_pct: number;
    hours_per_week: number;
    weeks_remaining: number;
  }) =>
    request<{ predicted_score: number; range: [number, number]; rationale: string }>(
      "/score/predict",
      { method: "POST", body: JSON.stringify(input) },
    ),
  summary: () => request<SessionSummary>("/user/summary"),
  /** Latest videos from curated GMAT YouTube channels (requires YOUTUBE_API_KEY on API). */
  learnVideos: (count = 2) =>
    request<LearnVideosResponse>(`/content/videos?count=${count}`),
  youtubeConfigured: () =>
    request<{ configured: boolean }>("/content/videos/status"),
  examCatalog: () =>
    request<{ official: OfficialExamResource[]; sigma: ExamBlueprintSummary[] }>(
      "/exam/catalog",
    ),
  examStart: (examId: string) =>
    request<ExamQuestionResponse>("/exam/start", {
      method: "POST",
      body: JSON.stringify({ exam_id: examId }),
    }),
  examSession: (sessionId: string) =>
    request<ExamSessionState>(`/exam/${sessionId}`),
  examCurrentQuestion: (sessionId: string) =>
    request<ExamQuestionResponse>(`/exam/${sessionId}/question`),
  examSubmitAnswer: (sessionId: string, answer: string, timeSeconds = 0) =>
    request<ExamSubmitAnswerResponse>(`/exam/${sessionId}/answer`, {
      method: "POST",
      body: JSON.stringify({ answer, time_seconds: timeSeconds }),
    }),
  examResume: (sessionId: string) =>
    request<ExamSessionState>(`/exam/${sessionId}/resume`, { method: "POST" }),
  examResults: (sessionId: string) =>
    request<ExamResultsResponse>(`/exam/${sessionId}/results`),
  explainQuestion: (body: {
    stem: string;
    choices: { label: string; text: string }[];
    correct_answer?: string;
    passage?: string;
    topic?: string;
    student_answer?: string;
  }) =>
    request<{ explanation: string; spoken?: string; verified?: string; adapter_used: boolean }>(
      "/practice/explain",
      { method: "POST", body: JSON.stringify(body) },
    ),
  /** Free-form explain: pasted JSON, word problems, or "explain this". */
  tutorExplainText: (text: string, studentAnswer?: string) =>
    request<{
      explanation: string;
      spoken?: string;
      verified?: string;
      adapter_used: boolean;
      stem?: string;
    }>("/tutor/explain-text", {
      method: "POST",
      body: JSON.stringify({ text, student_answer: studentAnswer }),
    }),
  agentGenerateMock: (
    sections: { id: "quant" | "verbal" | "di"; count: number }[],
    useLlm = true,
  ) =>
    request<GeneratedExamResponse>("/agents/mock/generate", {
      method: "POST",
      body: JSON.stringify({ sections, use_llm: useLlm }),
    }),
  agentRetrieve: (q: string, k = 5, section?: string) =>
    request<{ query: string; hits: unknown[] }>(
      `/agents/retrieve?q=${encodeURIComponent(q)}&k=${k}${section ? `&section=${section}` : ""}`,
    ),
  bankStats: () => request<BankStats>("/agents/bank/stats"),
  recordAttempt: (attempt: AttemptCreate) =>
    request<AttemptRecord>("/attempts", {
      method: "POST",
      body: JSON.stringify(attempt),
    }),
  listAttempts: (studentId: string) =>
    request<{ attempts: AttemptRecord[] }>(
      `/attempts?student_id=${encodeURIComponent(studentId)}`,
    ),
  clearAttempts: (studentId: string) =>
    request<{ deleted: number }>(
      `/attempts?student_id=${encodeURIComponent(studentId)}`,
      { method: "DELETE" },
    ),
  speakSsml: (text: string) =>
    request<{ ssml: string }>("/speech/ssml", {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
};

export { ApiError };
